import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bed, Bath, Ruler, Trees, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { publicApi } from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";

const nfEUR = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

export default function Rentals() {
  const { t, localized } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const area = params.get("area") || "all";
  const type = params.get("type") || "all";

  useEffect(() => {
    setLoading(true);
    publicApi
      .listProperties({ is_rental: true, country: "Spain", limit: 100 })
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const areas = useMemo(() => Array.from(new Set(items.map((x) => x.location))), [items]);
  const filtered = items.filter(
    (x) => (area === "all" || x.location === area) && (type === "all" || x.type === type)
  );

  function setArea(a) {
    const next = new URLSearchParams(params);
    if (a === "all") next.delete("area");
    else next.set("area", a);
    setParams(next);
  }
  function setType(t) {
    const next = new URLSearchParams(params);
    if (t === "all") next.delete("type");
    else next.set("type", t);
    setParams(next);
  }

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="overline mb-3">{t("rentals.label")}</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-ink font-light max-w-4xl">
          {t("rentals.title")}
        </h1>
        <p className="mt-8 text-lg text-ink max-w-2xl leading-relaxed font-normal">
          {t("rentals.body")}
        </p>

        {areas.length > 0 && (
          <div className="mt-14 space-y-4 pb-6 border-b border-line">
            <div className="flex flex-wrap items-center gap-2">
              <span className="overline mr-3">{t("rentals_page.filter_type")}:</span>
              {[
                ["all", t("rentals_page.type_all")],
                ["villa", t("properties.villa")],
                ["apartment", t("properties.apartment")],
                ["penthouse", t("properties.penthouse")],
                ["townhouse", t("properties.townhouse")],
                ["finca", t("properties.finca")],
                ["chalet", t("properties.chalet")],
              ].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setType(val)}
                  data-testid={`rentals-filter-type-${val}`}
                  className={`text-sm px-3 py-1 border transition-colors ${
                    type === val ? "border-ink bg-ink text-bg" : "border-line hover:border-ink"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="overline mr-3">{t("rentals.filter_country")}:</span>
              <button
                onClick={() => setArea("all")}
                data-testid="rentals-filter-all"
                className={`text-sm px-3 py-1 border transition-colors ${
                  area === "all" ? "border-ink bg-ink text-bg" : "border-line hover:border-ink"
                }`}
              >
                {t("properties.filter_all")}
              </button>
              {areas.map((a) => (
                <button
                  key={a}
                  onClick={() => setArea(a)}
                  data-testid={`rentals-filter-${a}`}
                  className={`text-sm px-3 py-1 border transition-colors ${
                    area === a ? "border-ink bg-ink text-bg" : "border-line hover:border-ink"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-line" />
                  <div className="h-4 mt-4 bg-line w-1/3" />
                  <div className="h-6 mt-2 bg-line w-2/3" />
                </div>
              ))
            : filtered.map((p, i) => (
                <RentalCard
                  key={p.id}
                  p={p}
                  localized={localized}
                  t={t}
                  index={i}
                />
              ))}
        </div>

        <div
          id="rentals-inquiry"
          className="mt-24 border-t border-line pt-16 grid md:grid-cols-12 gap-10"
        >
          <div className="md:col-span-5">
            <div className="overline mb-3">{t("rentals.cta")}</div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-ink font-light">
              {t("rentals_page.availability")}
            </h2>
            <p className="mt-4 text-muted text-sm leading-relaxed max-w-md">
              {t("rentals_page.availability_body")}
            </p>
          </div>
          <div className="md:col-span-7">
            <InquiryForm testIdPrefix="rentals-inquiry" service="rental" />
          </div>
        </div>
      </div>
    </div>
  );
}

function RentalCard({ p, localized, t, index }) {
  const period =
    p.rental_period === "night"
      ? t("rentals.per_night")
      : p.rental_period === "month"
        ? t("rentals.per_month")
        : t("rentals.per_week");
  return (
    <article
      data-testid={`rental-card-${p.reference || p.id}`}
      className="group bg-white border border-line hover:border-ink transition-colors"
      style={{ animation: "fadeUp .9s ease-out both", animationDelay: `${index * 80}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={p.images?.[0]}
          alt=""
          loading="lazy"
          className="card-img absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3 bg-white/90 text-ink text-[10px] tracking-widest2 uppercase px-3 py-1">
          {((t(`properties.${p.type === "plot" ? "plot_land" : p.type}`) || p.type || "Villa")).replace(/s$/i, "")}{" "}
          · Mallorca
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-1.5 text-muted text-xs">
          <MapPin size={12} strokeWidth={1.6} />
          <span className="uppercase tracking-widest2">{p.location}</span>
        </div>
        <h3 className="font-serif text-xl mt-2 leading-tight text-ink group-hover:text-accent transition-colors line-clamp-2 min-h-[3.2em]">
          {localized(p.title)}
        </h3>

        <div className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4 text-sm border-t border-line pt-4">
          <Spec icon={<Bed size={15} strokeWidth={1.6} />} value={p.bedrooms} label={t("properties.bedrooms")} />
          <Spec icon={<Bath size={15} strokeWidth={1.6} />} value={p.bathrooms} label={t("properties.bathrooms")} />
          <Spec icon={<Ruler size={15} strokeWidth={1.6} />} value={p.area_m2} label="m²" />
          {p.plot_m2 > 0 && (
            <Spec icon={<Trees size={15} strokeWidth={1.6} />} value={p.plot_m2} label="m² plot" />
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-line flex items-end justify-between">
          <div>
            <div className="overline">{t("rentals.from")}</div>
            <div className="font-mono text-lg text-ink leading-none mt-1">
              {p.rent_price ? nfEUR.format(p.rent_price) : "—"}
            </div>
          </div>
          <div className="text-[11px] text-muted uppercase tracking-widest2">
            {period}
          </div>
        </div>
      </div>
    </article>
  );
}

function Spec({ icon, value, label }) {
  return (
    <div className="flex items-center gap-2 text-ink">
      <span className="text-muted">{icon}</span>
      <span className="font-mono text-[13px] leading-none">{value}</span>
      <span className="text-muted text-[11px] leading-none">{label}</span>
    </div>
  );
}
